import { gql } from '@apollo/client';
import {
    FR_INSPECTION_ENTRY_FIELDS,
    FR_INSPECTION_ENTRY_TASK_FIELDS,
    FR_INSPECTION_LIST_FIELDS,
    FR_INSPECTION_LIST_NAME_ID_FIELDS,
    FR_INSPECTION_MACHINE_FIELDS_FRAGMENT,
    FR_INSPECTION_TASK_FIELDS
} from './fragments/inspection';
import { recurrentMeterType_fields } from './service';
import { FR_ASSET_FIELDS } from './fragments/asset';

const inspectionType_fields = `
	InspectionTypeId
    Name
`;

const inspectionEntryStatus_fields = `
	InspectionEntryStatusId
    Name
`;

export const machine_fields = `
	MachineId
	Name
	SerialNbr
	Organization {
		OrganizationId
		Name
	}
	MachineType {
		Name
		MachineTypeId
	}
	Asset {
		AssetName
		AssetType
	}
`;

const inspectionList_fields = `
	InspectionListId
    fi_name: Name(LangCode: FI)
	sv_name: Name(LangCode: SV)
    en_name: Name(LangCode: EN)
	no_name: Name(LangCode: NO)
    fi_description: Description(LangCode: FI)
	sv_description: Description(LangCode: SV)
	en_description: Description(LangCode: EN)
	no_description: Description(LangCode: NO)
	InspectionListType
	RecurrentMeterType{
		${recurrentMeterType_fields}
	}
	RecurrentType{
		Name
	}
	InspectionTask{
        InspectionTaskId
		Name
		sv_name: Name(LangCode: SV)
    	en_name: Name(LangCode: EN)
		no_name: Name(LangCode: NO)
		Order
		ServiceWork {
			Name
			sv_name: Name(LangCode: SV)
    		en_name: Name(LangCode: EN)
			no_name: Name(LangCode: NO)
		}
		InspectionItem {
			InspectionItemId
			Category {
				CategoryId
				Name
				sv_name: Name(LangCode: SV)
				en_name: Name(LangCode: EN)
				no_name: Name(LangCode: NO)
			}
			ServiceWork {
				ServiceWorkId
				Name
				sv_name: Name(LangCode: SV)
				en_name: Name(LangCode: EN)
				no_name: Name(LangCode: NO)
			}
			Assembly {
				AssemblyId
				Name
				sv_name: Name(LangCode: SV)
				en_name: Name(LangCode: EN)
				no_name: Name(LangCode: NO)
			}
			Component {
				ComponentId
				Name
				sv_name: Name(LangCode: SV)
				en_name: Name(LangCode: EN)
				no_name: Name(LangCode: NO)
			}
		}
    }
	Machine {
		${machine_fields}
	}
	MachineInspection {
		MachineInspectionId
		MachineId
		Mandatory
		InspectionListId
		Created
		Updated
		StartDate
		EndDate
	}
	InspectionWorkflow {
		InspectionWorkflowId
		InspectionTask {
			InspectionTaskId
		}
		Enabled
		Email
		WorkflowAction {
			WorkflowActionId
			Name
		}
		WorkflowTrigger {
			WorkflowTriggerId
			Name
		}
		Created
		Updated
	}
	RecurrentCycle
    Created
    Updated
	Access {  
		Read
		Create
		Delete
		Update
	}
	OrganizationAccess {
		Organization {
			OrganizationId
			Name
		}
		OrganizationAccessType {
			__typename
			... on InspectionListOrganizationType {
				InspectionListOrganizationTypeId
				Name
			}
		}
	}
`;

/* -----------------------  ---------------------------------------  */

export const GET_INSPECTION_TYPES = gql`
query inspectionTypes{ 
	inspectionTypes{
		${inspectionType_fields}
	}
}`;

export const GET_INSPECTION_ENTRYSTATUSES = gql`
query inspectionEntryStatuses{ 
	inspectionEntryStatuses{
		${inspectionEntryStatus_fields}
	}
}`;

export const GET_CONDITION_TYPES = gql`
    query conditionTypes {
        conditionTypes {
            ConditionTypeId
            Name
        }
    }
`;

/* ----------------------- inspectionTask ---------------------------------------  */

export const GET_INSPECTION_TASK_ROWS = gql`
    query getInspectionTaskRows(
        $InspectionTaskId: [Int!]
        $From: Int
        $To: Int
        $Search: String
        $InspectionTaskType: InspectionTaskTypeEnum
    ) {
        inspectionTaskRows(
            data: {
                InspectionTaskId: $InspectionTaskId
                From: $From
                To: $To
                Search: $Search
                InspectionTaskType: $InspectionTaskType
            }
        ) {
            TotalRowCount
            InspectionTask {
                ...fr_InspectionTaskFields
            }
        }
    }
    ${FR_INSPECTION_TASK_FIELDS}
`;

export const GET_INSPECTION_TASK_AUTOCOMPLETE_ROWS = gql`
    query getInspectionTaskAutoCompleteRows(
        $InspectionTaskId: [Int!]
        $From: Int
        $To: Int
        $Search: String
        $InspectionTaskType: InspectionTaskTypeEnum
    ) {
        inspectionTaskAutoCompleteRows(
            data: {
                InspectionTaskId: $InspectionTaskId
                From: $From
                To: $To
                Search: $Search
                InspectionTaskType: $InspectionTaskType
            }
        ) {
            TotalRowCount
            InspectionTask {
                ...fr_InspectionTaskFields
            }
        }
    }
    ${FR_INSPECTION_TASK_FIELDS}
`;

export const GET_ASSESSMENT_TASK_ROWS = gql`
    query getAssessmentTaskRows(
        $InspectionTaskId: [Int!]
        $From: Int
        $To: Int
        $Search: String
        $InspectionTaskType: InspectionTaskTypeEnum
    ) {
        assessmentTaskRows(
            data: {
                InspectionTaskId: $InspectionTaskId
                From: $From
                To: $To
                Search: $Search
                InspectionTaskType: $InspectionTaskType
            }
        ) {
            TotalRowCount
            InspectionTask {
                ...fr_InspectionTaskFields
            }
        }
    }
    ${FR_INSPECTION_TASK_FIELDS}
`;

export const GET_INSPECTION_TASKS = gql`
    query inspectionTasks {
        inspectionTasks {
            ...fr_InspectionTaskFields
        }
    }
    ${FR_INSPECTION_TASK_FIELDS}
`;

export const CREATE_INSPECTION_TASK = gql`
    mutation createInspectionTask(
        $Name: [LangCodeTextInputType!]!
        $Instruction: [LangCodeTextInputType!]
        $InspectionType: InspectionTypeEnum!
        $AddOptions: [CreateInspectionOptions!]
        $InspectionItem: InspectionItemInputType
        $AddOrganizations: [Int!]
        $InspectionTaskType: InspectionTaskTypeEnum
        $Private: Boolean
    ) {
        createInspectionTask(
            data: {
                Name: $Name
                Instruction: $Instruction
                InspectionType: $InspectionType
                AddOptions: $AddOptions
                InspectionItem: $InspectionItem
                AddOrganizations: $AddOrganizations
                InspectionTaskType: $InspectionTaskType
                Private: $Private
            }
        ) {
            ...fr_InspectionTaskFields
        }
    }
    ${FR_INSPECTION_TASK_FIELDS}
`;

export const UPDATE_INSPECTION_TASK = gql`
    mutation updateInspectionTask(
        $InspectionTaskId: Int!
        $Name: [LangCodeTextInputType!]
        $Instruction: [LangCodeTextInputType!]
        $UpdateOptions: [UpdateInspectionOptions!]
        $AddInspectionOptions: [AddInspectionOption!]
        $RemoveInspectionOptions: [RemoveInspectionOption!]
        $InspectionItem: InspectionItemInputType
        $AddOrganizations: [Int!]
        $RemoveOrganizations: [Int!]
        $Private: Boolean
    ) {
        updateInspectionTask(
            data: {
                InspectionTaskId: $InspectionTaskId
                Name: $Name
                Instruction: $Instruction
                UpdateOptions: $UpdateOptions
                AddInspectionOptions: $AddInspectionOptions
                RemoveInspectionOptions: $RemoveInspectionOptions
                AddOrganizations: $AddOrganizations
                RemoveOrganizations: $RemoveOrganizations
                InspectionItem: $InspectionItem
                Private: $Private
            }
        ) {
            ...fr_InspectionTaskFields
        }
    }
    ${FR_INSPECTION_TASK_FIELDS}
`;

export const DELETE_INSPECTION_TASK = gql`
    mutation deleteInspectionTask($InspectionTaskId: Int!) {
        deleteInspectionTask(data: { InspectionTaskId: $InspectionTaskId })
    }
`;

/* ----------------------- createInspectionList ---------------------------------------  */

export const GET_INSPECTION_LIST_ROWS = gql`
    query getInspectionListRows(
        $InspectionListId: Int
        $MachineId: Int
        $From: Int
        $To: Int
        $TextSearch: TextSearchInputType
        $InspectionListType: InspectionListTypeEnum
    ) {
        inspectionListRows(
            data: {
                InspectionListId: $InspectionListId
                MachineId: $MachineId
                From: $From
                To: $To
                TextSearch: $TextSearch
                InspectionListType: $InspectionListType
            }
        ) {
            TotalRowCount
            InspectionList {
                ...fr_InspectionListNameIdFields
            }
        }
    }
    ${FR_INSPECTION_LIST_NAME_ID_FIELDS}
`;

export const GET_INSPECTION_ENTRY_ROWS = gql`
    query getInspectionEntryRows(
        $MachineId: Int
        $InspectionEntryId: [Int!]
        $From: Int
        $To: Int
        $InspectionEntryStatus: [InspectionEntryStatusEnum!]
    ) {
        inspectionEntryRows(
            data: {
                MachineId: $MachineId
                InspectionEntryId: $InspectionEntryId
                From: $From
                To: $To
                InspectionEntryStatus: $InspectionEntryStatus
            }
        ) {
            TotalRowCount
            InspectionEntry {
                ...fr_InspectionEntryFields
            }
        }
    }
    ${FR_INSPECTION_ENTRY_FIELDS}
`;

export const GET_ASSESSMENT_ENTRY_ROWS = gql`
    query getAssessmentEntryRows(
        $MachineId: Int
        $InspectionEntryId: [Int!]
        $From: Int
        $To: Int
        $InspectionEntryStatus: [InspectionEntryStatusEnum!]
    ) {
        assessmentEntryRows(
            data: {
                MachineId: $MachineId
                InspectionEntryId: $InspectionEntryId
                From: $From
                To: $To
                InspectionEntryStatus: $InspectionEntryStatus
            }
        ) {
            TotalRowCount
            InspectionEntry {
                ...fr_InspectionEntryFields
            }
        }
    }
    ${FR_INSPECTION_ENTRY_FIELDS}
`;

export const GET_INSPECTION_ENTRY = gql`
    query getInspectionEntry($InspectionEntryId: Int!) {
        getInspectionEntry(data: { InspectionEntryId: $InspectionEntryId }) {
            ...fr_InspectionEntryFields
        }
    }
    ${FR_INSPECTION_ENTRY_FIELDS}
`;

export const GET_LATEST_INSPECTION_ENTRY = gql`
    query latestInspectionEntry($MachineId: Int, $ProjectId: Int, $InspectionListId: Int!) {
        latestInspectionEntry(
            data: {
                MachineId: $MachineId
                ProjectId: $ProjectId
                InspectionListId: $InspectionListId
            }
        ) {
            ...fr_InspectionEntryFields
        }
    }
    ${FR_INSPECTION_ENTRY_FIELDS}
`;

export const GET_ASSESSMENT_ENTRY = gql`
    query getAssessmentEntry($InspectionEntryId: Int!) {
        getAssessmentEntry(data: { InspectionEntryId: $InspectionEntryId }) {
            ...fr_InspectionEntryFields
        }
    }
    ${FR_INSPECTION_ENTRY_FIELDS}
`;

export const ARCHIVE_INSPECTION_ENTRY = gql`
    mutation archiveInspectionEntry($InspectionEntryId: Int!) {
        archiveInspectionEntry(data: { InspectionEntryId: $InspectionEntryId }) {
            ...fr_InspectionEntryFields
        }
    }
    ${FR_INSPECTION_ENTRY_FIELDS}
`;

export const GET_INSPECTION_LISTS = gql`
    query inspectionLists(
        $MachineId: Int
        $ProjectId: Int
        $EquipmentId: Int
        $InspectionListType: InspectionListTypeEnum
    ) {
        inspectionLists(
            data: {
                MachineId: $MachineId
                ProjectId: $ProjectId
                EquipmentId: $EquipmentId
                InspectionListType: $InspectionListType
            }
        ) {
            ...fr_InspectionListFields
            LatestInspectionEntry(data: { ProjectId: $ProjectId, MachineId: $MachineId }) {
                InspectionEntryId
                InspectionEntryStatus {
                    InspectionEntryStatusId
                    Name
                }
                InspectionEntryTask {
                    InspectionEntryTaskId
                    InspectionIssue {
                        IssueId
                        IssueStatus {
                            IssueStatusId
                            Name
                        }
                    }
                }
            }
        }
    }
    ${FR_INSPECTION_LIST_FIELDS}
`;

export const GET_INSPECTION_LIST = gql`
    query inspectionList($InspectionListId: Int!) {
        inspectionList(data: { InspectionListId: $InspectionListId }) {
            ...fr_InspectionListFields
        }
    }
    ${FR_INSPECTION_LIST_FIELDS}
`;

export const ARCHIVE_INSPECTION_TASK = gql`
    mutation archiveInspectionTask($InspectionTaskId: Int!) {
        archiveInspectionTask(data: { InspectionTaskId: $InspectionTaskId }) {
            ...fr_InspectionTaskFields
        }
    }
    ${FR_INSPECTION_TASK_FIELDS}
`;

export const GET_ASSESSMENT_TASK = gql`
    query assessmentTask($InspectionTaskId: Int!) {
        assessmentTask(data: { InspectionTaskId: $InspectionTaskId }) {
            ...fr_InspectionTaskFields
        }
    }
    ${FR_INSPECTION_TASK_FIELDS}
`;
export const GET_INSPECTION_TASK = gql`
    query inspectionTask($InspectionTaskId: Int!) {
        inspectionTask(data: { InspectionTaskId: $InspectionTaskId }) {
            ...fr_InspectionTaskFields
        }
    }
    ${FR_INSPECTION_TASK_FIELDS}
`;
export const CREATE_INSPECTION_LIST = gql`
    mutation createInspectionList(
        $Name: [LangCodeTextInputType!]!
        $Description: [LangCodeTextInputType!]
        $RecurrentType: RecurrentTypeEnum
        $RecurrentMeterType: RecurrentMeterTypeEnum
        $RecurrentCycle: Int
        $AddInspectionTasks: [AddInspectionTaskInputType!]
        $CreateWorkflow: [CreateWorkflowInputType!]
        $AddOrganizations: [Int!]
        $AddMachines: [CreateInspectionAddMachines!]
        $InspectionListType: InspectionListTypeEnum
        $AddProjects: [CreateInspectionAddProject!]
        $Private: Boolean
        $AddEquipments: [CreateInspectionAddEquipment!]
    ) {
        createInspectionList(
            data: {
                Name: $Name
                Description: $Description
                RecurrentType: $RecurrentType
                RecurrentMeterType: $RecurrentMeterType
                RecurrentCycle: $RecurrentCycle
                AddInspectionTasks: $AddInspectionTasks
                CreateWorkflow: $CreateWorkflow
                AddOrganizations: $AddOrganizations
                AddMachines: $AddMachines
                InspectionListType: $InspectionListType
                AddProjects: $AddProjects
                Private: $Private
                AddEquipments: $AddEquipments
            }
        ) {
            ...fr_InspectionListFields
        }
    }
    ${FR_INSPECTION_LIST_FIELDS}
`;

export const UPDATE_INSPECTION_LIST = gql`
    mutation updateInspectionList(
        $InspectionListId: Int!
        $Name: [LangCodeTextInputType!]
        $Description: [LangCodeTextInputType!]
        $RecurrentType: RecurrentTypeEnum
        $RecurrentMeterType: RecurrentMeterTypeEnum
        $RecurrentCycle: Int
        $UpsertInspectionTasks: [UpsertInspectionTaskInputType!]
        $RemoveInspectionTasks: [Int!]
        $AddMachines: [CreateInspectionAddMachines!]
        $RemoveMachines: [Int!]
        $UpdateMachines: [UpdateInspectionUpdateMachineInputType!]
        $CreateWorkflows: [CreateWorkflowInputType!]
        $RemoveWorkflows: [RemoveWorkflowInputType!]
        $UpdateWorkflows: [UpdateWorkflowInputType!]
        $AddOrganizations: [Int!]
        $RemoveOrganizations: [Int!]
        $InspectionListType: InspectionListTypeEnum
        $AddProjects: [UpdateInspectionListAddProjects!]
        $RemoveProjects: [UpdateInspectionListRemoveProjects!]
        $Private: Boolean
        $AddEquipments: [CreateInspectionAddEquipment!]
        $RemoveEquipments: [CreateInspectionAddEquipment!]
    ) {
        updateInspectionList(
            data: {
                InspectionListId: $InspectionListId
                Name: $Name
                Description: $Description
                RecurrentType: $RecurrentType
                RecurrentMeterType: $RecurrentMeterType
                RecurrentCycle: $RecurrentCycle
                UpsertInspectionTasks: $UpsertInspectionTasks
                RemoveInspectionTasks: $RemoveInspectionTasks
                AddMachines: $AddMachines
                RemoveMachines: $RemoveMachines
                UpdateMachines: $UpdateMachines
                CreateWorkflows: $CreateWorkflows
                RemoveWorkflows: $RemoveWorkflows
                UpdateWorkflows: $UpdateWorkflows
                AddOrganizations: $AddOrganizations
                RemoveOrganizations: $RemoveOrganizations
                InspectionListType: $InspectionListType
                AddProjects: $AddProjects
                RemoveProjects: $RemoveProjects
                Private: $Private
                AddEquipments: $AddEquipments
                RemoveEquipments: $RemoveEquipments
            }
        ) {
            ...fr_InspectionListFields
        }
    }
    ${FR_INSPECTION_LIST_FIELDS}
`;

export const DELETE_INSPECTION_LIST = gql`
    mutation deleteInspectionList($InspectionListId: Int!) {
        deleteInspectionList(data: { InspectionListId: $InspectionListId })
    }
`;

export const ARCHIVE_INSPECTION_LIST = gql`
    mutation archiveInspectionList($InspectionListId: Int!) {
        archiveInspectionList(data: { InspectionListId: $InspectionListId }) {
            ...fr_InspectionListFields
        }
    }
    ${FR_INSPECTION_LIST_FIELDS}
`;

export const GET_INSPECTION_ENTRY_TASK_OPEN_ISSUES = gql`
    query inspectionEntryTaskOpenIssues(
        $InspectionListId: Int!
        $ProjectId: Int
        $MachineId: Int
        $EquipmentId: Int
    ) {
        inspectionEntryTaskOpenIssues(
            data: {
                InspectionListId: $InspectionListId
                ProjectId: $ProjectId
                MachineId: $MachineId
                EquipmentId: $EquipmentId
            }
        ) {
            ...fr_InspectionEntryTaskFields
        }
    }
    ${FR_INSPECTION_ENTRY_TASK_FIELDS}
`;

export const GET_INSPECTION_ENTRY_TASK_ROWS = gql`
    query getInspectionEntryTaskRows(
        $InspectionEntryTaskId: [Int!]
        $Success: Boolean
        $From: Int
        $To: Int
    ) {
        inspectionEntryTaskRows(
            data: {
                InspectionEntryTaskId: $InspectionEntryTaskId
                Success: $Success
                From: $From
                To: $To
            }
        ) {
            TotalRowCount
            InspectionEntryTask {
                ...fr_InspectionEntryTaskFields
                InspectionIssue {
                    IssueId
                    IssueStatus {
                        IssueStatusId
                        Name
                    }
                }
                InspectionEntry {
                    InspectionEntryId
                    Date
                    Created
                    Updated
                    InspectionEntryCreator {
                        UserId
                        Fullname
                    }
                    InspectionList {
                        InspectionListId
                        fi_name: Name(LangCode: FI)
                        sv_name: Name(LangCode: SV)
                        en_name: Name(LangCode: EN)
                        no_name: Name(LangCode: NO)
                    }
                    Asset {
                        ...fr_AssetFields
                    }
                    Project {
                        ProjectId
                        Name
                        ProjectNbr
                    }
                }
            }
        }
    }
    ${FR_ASSET_FIELDS}
    ${FR_INSPECTION_ENTRY_TASK_FIELDS}
`;

export const GET_ASSESSMENT_ENTRY_TASK_ROWS = gql`
    query getAssessmentEntryTaskRows(
        $InspectionEntryTaskId: [Int!]
        $Success: Boolean
        $From: Int
        $To: Int
    ) {
        assessmentEntryTaskRows(
            data: {
                InspectionEntryTaskId: $InspectionEntryTaskId
                Success: $Success
                From: $From
                To: $To
            }
        ) {
            TotalRowCount
            InspectionEntryTask {
                ...fr_InspectionEntryTaskFields
                InspectionIssue {
                    IssueId
                    IssueStatus {
                        IssueStatusId
                        Name
                    }
                }
                InspectionEntry {
                    InspectionEntryId
                    Date
                    Created
                    Updated
                    InspectionEntryCreator {
                        UserId
                        Fullname
                    }
                    InspectionList {
                        InspectionListId
                        fi_name: Name(LangCode: FI)
                        sv_name: Name(LangCode: SV)
                        en_name: Name(LangCode: EN)
                        no_name: Name(LangCode: NO)
                    }
                    Asset {
                        ...fr_AssetFields
                    }
                    Project {
                        ProjectId
                        Name
                        ProjectNbr
                    }
                }
            }
        }
    }
    ${FR_ASSET_FIELDS}
    ${FR_INSPECTION_ENTRY_TASK_FIELDS}
`;
